import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
} from "@mui/material";

export const CarePlanLayout = () => {
  const [selectedPlan, setSelectedPlan] = useState("Cardiac Care+");
  const [billingCycle, setBillingCycle] = useState("Monthly");
  const [zip, setZip] = useState("");
  const [agree, setAgree] = useState(false);

  const plans = [
    {
      name: "Essential Recovery",
      description: "For stable discharge with low risk",
      price: { Monthly: 179, Quarterly: 489 },
      details: [
        "Remote monitoring: Weekly 1",
        "Nurse visits: Monthly 1",
        "Telecardiology: Monthly 1",
        "Physio sessions: Monthly 1",
        "Medication delivery",
      ],
      color: "#edf3ff",
    },
    {
      name: "Cardiac Care+",
      description: "Best for most post-CABG patients",
      price: { Monthly: 299, Quarterly: 849 },
      details: [
        "Remote monitoring: Weekly 2",
        "Nurse visits: Monthly 2",
        "Telecardiology: Monthly 2",
        "Physio sessions: Monthly 2",
        "Medication delivery",
      ],
      color: "#f2f7ff",
      recommended: true,
    },
    {
      name: "HeartGuard Premium",
      description: "For higher risk or complex cases",
      price: { Monthly: 421, Quarterly: 1145 },
      details: [
        "Remote monitoring: Weekly 4",
        "Nurse visits: Monthly 4",
        "Telecardiology: Monthly 4",
        "Physio sessions: Monthly 4",
        "Medication delivery",
      ],
      color: "#fff4f4",
    },
  ];

  const insuranceCoverage = 0.2; // 20%
  const selected = plans.find((p) => p.name === selectedPlan);
  const planPrice = selected ? selected.price[billingCycle as "Monthly" | "Quarterly"] : 0;
  const discount = planPrice * insuranceCoverage;
  const total = planPrice - discount;

  return (
    <Box sx={{ width: "100%", bgcolor: "#fafbff"}}>
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Care Plan
      </Typography>
      <Typography variant="subtitle1" sx={{ color: "#555", mb: 3 }}>
        Care at Home
      </Typography>
      <Typography sx={{ color: "#666", mb: 3 }}>
        Discharge after heart surgery • Tailored packages to keep you safe, comfortable, and on track.
      </Typography>

      {/* Recommended and Insurance section */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Card
          sx={{
            flex: 1,
            minWidth: 280,
            bgcolor: "#eef5ff",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            ✅ Recommended: Cardiac Care+
          </Typography>
          <Typography variant="body2" sx={{ color: "#555" }}>
            Based on your vitals, medications, and length of stay. You can switch any time.
          </Typography>
        </Card>

        <Card
          sx={{
            flex: 1,
            minWidth: 280,
            bgcolor: "#eef5ff",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            💳 Insurance estimate: <span style={{ color: "#007bff" }}>20% covered</span>
          </Typography>
        </Card>
      </Box>

      {/* Plan Cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        {plans.map((plan) => (
          <Card
            key={plan.name}
            onClick={() => setSelectedPlan(plan.name)}
            sx={{
              flex: 1,
              minWidth: 280,
              borderRadius: 2,
              cursor: "pointer",
              border: selectedPlan === plan.name ? "2px solid #007bff" : "1px solid #ddd",
              bgcolor: plan.color,
              boxShadow: selectedPlan === plan.name ? "0 0 8px rgba(0,123,255,0.2)" : "none",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {plan.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                {plan.description}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                ${plan.price.Monthly}/mo
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                ${plan.price.Quarterly}/qtr
              </Typography>

              {plan.details.map((d, i) => (
                <Typography key={i} variant="body2" sx={{ color: "#333" }}>
                  {i + 1}. {d}
                </Typography>
              ))}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Selection Summary */}
      <Box
        sx={{
          border: "1px solid #cfdcff",
          borderRadius: 2,
          p: 3,
          bgcolor: "#fff",
          mx: "auto",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Your selection
        </Typography>

        <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
          {selected?.name}
        </Typography>
        <Typography variant="body2" sx={{ color: "#777", mb: 2 }}>
          {selected?.description}
        </Typography>

        <RadioGroup
          row
          value={billingCycle}
          onChange={(e) => setBillingCycle(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="Monthly" control={<Radio />} label="Monthly" />
          <FormControlLabel value="Quarterly" control={<Radio />} label="Quarterly" />
        </RadioGroup>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" sx={{ color: "#666" }}>
          Plan ({billingCycle})
        </Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          ${planPrice}
        </Typography>
        <Typography variant="body2" sx={{ color: "#888" }}>
          Estimated insurance -${discount.toFixed(0)}
        </Typography>

        <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
          Total due today: ${total.toFixed(0)}
        </Typography>

        <TextField
          label="ZIP Code"
          fullWidth
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          sx={{ mt: 3 }}
        />

        <FormControlLabel
          control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
          label="I consent to share my discharge summary and vitals for home-care coordination."
          sx={{ mt: 2 }}
        />

        <Button
          fullWidth
          variant="contained"
          sx={{
            mt: 3,
            py: 1.2,
            bgcolor: "#007bff",
            "&:hover": { bgcolor: "#0065d1" },
            borderRadius: "10px",
            fontWeight: 600,
          }}
          disabled={!agree || !zip}
        >
          Proceed to Secure Checkout
        </Button>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 2, color: "#777", textAlign: "center" }}
        >
          By continuing, you agree to our <u>Terms & Privacy Policy</u>.
        </Typography>
      </Box>
    </Box>
  );
};
